import { api_request } from '../axios_client';
import { handleError } from '../common';
import * as Constants from '../constants';

const isSI = true;

/**
 * TASK-4450 — Bulk download of appointment files by LR number.
 *
 * The backend responds with a binary body:
 *  - On success: a ZIP archive whose top-level folders are named by LR number.
 *  - On any processing error: an enriched CSV echoing the uploaded rows with an
 *    appended error column (may arrive on a 2xx OR on a 4xx/5xx error status).
 *
 * This client owns the RESPONSE-TYPE BRANCHING (task 13): callers do not inspect
 * headers themselves. `downloadBulkAppointmentsByLR` returns a normalized result
 * describing which kind of body came back, the blob, and the server-suggested
 * filename, so the UI layer only decides how to present it.
 */

// Response kinds surfaced to the UI layer.
export const BULK_APPOINTMENT_RESULT = {
  ZIP: 'zip', // success — ZIP of appointment files
  ERROR_CSV: 'error_csv', // processing failure — enriched error CSV
  UNKNOWN: 'unknown' // unrecognized body — let the user save it rather than drop it
};

/**
 * Extract the filename from a Content-Disposition header, if present.
 * Handles both `filename="x.zip"` and RFC 5987 `filename*=UTF-8''x.zip`.
 *
 * @param {string} [contentDisposition]
 * @returns {string|undefined}
 */
const parseContentDispositionFilename = (contentDisposition) => {
  if (!contentDisposition) return undefined;
  // RFC 5987 extended form takes precedence when present.
  const extended = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(contentDisposition);
  if (extended && extended[1]) {
    try {
      return decodeURIComponent(extended[1].trim().replace(/^"|"$/g, ''));
    } catch {
      return extended[1].trim().replace(/^"|"$/g, '');
    }
  }
  const basic = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return basic && basic[1] ? basic[1].trim() : undefined;
};

/**
 * Classify a response's body from its headers and, when headers are ambiguous,
 * from the payload itself.
 *
 * Note: `Content-Disposition` is visible in the browser Network tab but is NOT
 * readable from JavaScript unless the API lists it in
 * `Access-Control-Expose-Headers`. This endpoint currently exposes only paging
 * and correlation headers, so we must sniff ZIP magic bytes when the server
 * sends `application/octet-stream`.
 *
 * @param {string} contentType
 * @param {string} [fileName] - filename hint from Content-Disposition.
 * @param {Blob} [blob]
 * @returns {Promise<'zip'|'error_csv'|'unknown'>}
 */
const isZipBlob = async (blob) => {
  if (!blob || blob.size < 4) return false;
  try {
    const chunk = blob.slice(0, 4);
    let buf;
    if (typeof chunk.arrayBuffer === 'function') {
      buf = await chunk.arrayBuffer();
    } else {
      buf = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsArrayBuffer(chunk);
      });
    }
    const header = new Uint8Array(buf);
    // PK\x03\x04 (local file), PK\x05\x06 (empty archive), PK\x07\x08 (spanned)
    return (
      header[0] === 0x50 &&
      header[1] === 0x4b &&
      (header[2] === 0x03 || header[2] === 0x05 || header[2] === 0x07)
    );
  } catch {
    return false;
  }
};

const classifyBody = async (contentType, fileName, blob) => {
  const ct = (contentType || '').toLowerCase();
  const name = (fileName || '').toLowerCase();
  if (ct.includes('zip') || name.endsWith('.zip')) return BULK_APPOINTMENT_RESULT.ZIP;
  if (ct.includes('csv') || ct.includes('text/csv') || name.endsWith('.csv')) {
    return BULK_APPOINTMENT_RESULT.ERROR_CSV;
  }
  if (blob && (await isZipBlob(blob))) return BULK_APPOINTMENT_RESULT.ZIP;
  return BULK_APPOINTMENT_RESULT.UNKNOWN;
};

/**
 * Some servers return the enriched error CSV with a 4xx/5xx status and a JSON
 * error body encoded as a Blob. Read a Blob body back into text so a JSON error
 * message can be surfaced to the user.
 *
 * @param {Blob} blob
 * @returns {Promise<string|undefined>}
 */
const readBlobText = async (blob) => {
  if (!blob || typeof blob.text !== 'function') return undefined;
  try {
    return await blob.text();
  } catch {
    return undefined;
  }
};

const buildTimestampedName = (kind) => {
  const ts = new Date().toISOString().replace(/[:.]/g, '-');
  if (kind === BULK_APPOINTMENT_RESULT.ZIP) return `appointment-files-${ts}.zip`;
  if (kind === BULK_APPOINTMENT_RESULT.ERROR_CSV) return `lr-appointment-errors-${ts}.csv`;
  return `bulk-appointment-response-${ts}`;
};

const EXT_BY_KIND = {
  [BULK_APPOINTMENT_RESULT.ZIP]: '.zip',
  [BULK_APPOINTMENT_RESULT.ERROR_CSV]: '.csv'
};

/**
 * Ensure the download name carries the extension implied by the classified body
 * kind. The backend sometimes omits the extension in Content-Disposition even
 * when the payload is a ZIP or CSV.
 *
 * @param {string} kind
 * @param {string|undefined} fileName
 * @returns {string}
 */
const normalizeFileName = (kind, fileName) => {
  const base = fileName || buildTimestampedName(kind);
  const ext = EXT_BY_KIND[kind];
  if (!ext || base.toLowerCase().endsWith(ext)) return base;
  return `${base}${ext}`;
};

/**
 * Upload a CSV of LR numbers and return a normalized, already-classified result.
 *
 * On a 2xx the body is a ZIP (success) or an enriched error CSV (partial/whole
 * failure). On a non-2xx that still carries a CSV/ZIP body (some backends signal
 * "processed with errors" via 422), the body is classified the same way. A
 * non-2xx carrying a JSON error is thrown so the UI shows a hard-failure toast.
 *
 * @param {File} file - CSV file of LR numbers.
 * @param {Object} [opts]
 * @param {AbortSignal} [opts.signal]
 * @param {Object} [opts.options] - extra axios options.
 * @returns {Promise<{ kind: string, blob: Blob, fileName: string }>}
 */
const downloadBulkAppointmentsByLR = async (file, { signal, options } = {}) => {
  const resourcePath = `${Constants.PATH.SALES.BULK_APPOINTMENT_DOWNLOAD.download}`;

  const formData = new FormData();
  formData.append('file', file);

  let res;
  try {
    res = await api_request({
      methodType: 'post',
      resourcePath,
      data: formData,
      // Binary body — ZIP on success, enriched CSV on failure.
      options: { responseType: 'blob', ...(options || {}) },
      signal,
      isSI
    });
  } catch (err) {
    // A non-2xx may still carry a classifiable CSV/ZIP body (e.g. 422 = processed
    // with errors). Reclassify it instead of treating it as a hard failure.
    const errRes = err?.response;
    const errCt = errRes?.headers?.['content-type'] || '';
    const errFileName = parseContentDispositionFilename(errRes?.headers?.['content-disposition']);
    const kind = await classifyBody(errCt, errFileName, errRes?.data);
    if (errRes?.data && kind !== BULK_APPOINTMENT_RESULT.UNKNOWN) {
      return {
        kind,
        blob: errRes.data,
        fileName: normalizeFileName(kind, errFileName)
      };
    }
    // Genuine hard failure (network, auth, JSON error body). Surface a readable
    // message where possible, then let the centralized handler run.
    if (errRes?.data) {
      const text = await readBlobText(errRes.data);
      if (text) {
        try {
          const parsed = JSON.parse(text);
          err.message = parsed?.message || parsed?.error || err.message;
        } catch {
          err.message = text.slice(0, 300) || err.message;
        }
      }
    }
    handleError(err);
    throw err;
  }

  const contentType = res?.headers?.['content-type'] || '';
  const fileName = parseContentDispositionFilename(res?.headers?.['content-disposition']);
  const kind = await classifyBody(contentType, fileName, res?.data);

  return {
    kind,
    blob: res?.data,
    fileName: normalizeFileName(kind, fileName)
  };
};

/**
 * Fetch a presigned URL (or equivalent) for the LR-numbers sample CSV template.
 *
 * @param {Object} [opts]
 * @param {AbortSignal} [opts.signal]
 * @param {Object} [opts.options] - extra axios options.
 * @returns {Promise<string>}
 */
const downloadBulkAppointmentSample = async ({ signal, options } = {}) => {
  const resourcePath = `${Constants.PATH.SALES.BULK_APPOINTMENT_DOWNLOAD.downloadSample}`;

  try {
    const res = await api_request({
      methodType: 'get',
      resourcePath,
      signal,
      options,
      isSI
    });
    const body = res?.data;
    if (body?.status?.statusCode !== 5000) {
      throw new Error(body?.status?.statusMessage || 'Unable to download sample file');
    }
    const payload = body?.data;
    if (typeof payload === 'string') return payload;
    if (payload?.link) return payload.link;
    throw new Error('Sample file link missing from server response');
  } catch (err) {
    handleError(err);
    throw err;
  }
};

export { downloadBulkAppointmentsByLR, downloadBulkAppointmentSample };
